/*
 * Free gift products
 * ------------------------------------------------------------------
 * A product can grant one or several gifts through its `custom.custom_free_gift`
 * list metafield (or the older single `custom.free_gift`). Whenever the cart changes we
 * reconcile the cart against what the metafields ask for:
 *
 *   - a gift line is added for every gift of every product in the cart
 *   - each gift quantity always mirrors the quantity of the product granting it
 *   - the gift lines are removed as soon as their parent leaves the cart
 *
 * Gift lines are marked with the `_gift_for` line item property (holding the
 * variant id of the product that granted them) so they stay separate from the
 * same product bought normally. sections/api-cart-gifts.liquid resolves the
 * metafields server side, snippets/cart-line-items.liquid renders the gift row
 * without quantity and remove controls.
 *
 * Cart value gifts: once the cart value (gift lines and products listed in
 * snippets/cart-gift-excluded-ids.liquid excluded) reaches the
 * threshold set in theme settings > Cart, the gifts picked there (one of each)
 * are added -- but only when no cart line grants a product metafield gift.
 * Product gifts from `custom.custom_free_gift` always win. Their lines
 * carry `_gift_for: cart_value` and are removed again when the cart drops
 * below the threshold (or a product gift appears). A second level
 * (`cart_value_gift_threshold_2`) adds its own gifts on top of the first ones,
 * marked `_gift_for: cart_value_2`.
 *
 * Every gift should have quantity 1 per level, but two syncs running at once
 * (two tabs, a page left mid-sync) both add the same gift and Shopify merges
 * them into one line with quantity 2. So a sync re-reads the cart after its
 * writes, runs again when the tab comes back into view, and the checkout
 * button waits for one last sync before leaving the cart.
 *
 * Pricing lives outside the theme: a line item is always added at the product's
 * own price, so any gift that is a regular catalog product needs a matching
 * "Buy X get Y" automatic discount in the admin to reach 0 -- with several gifts
 * the discount has to cover every one of them. Without it the gift
 * is added but stays payable -- the cart shows its price so that is visible.
 */

(function () {
  var SECTION_ID = 'api-cart-gifts';
  var GIFT_PROPERTY = '_gift_for';
  var CART_VALUE_PARENT = 'cart_value';
  var CART_VALUE_PARENT_2 = 'cart_value_2';

  var MAX_PASSES = 3;
  var CHECKOUT_WAIT = 5000;

  var syncing = false;
  var rerun = false;
  var selfDispatch = false;
  var pending = Promise.resolve();

  function rootUrl() {
    if (window.theme && window.theme.routes && window.theme.routes.root_url) {
      return window.theme.routes.root_url;
    }
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartUrl() {
    if (window.theme && window.theme.routes && window.theme.routes.cart) {
      return window.theme.routes.cart;
    }
    return '/cart';
  }

  function postJSON(url, body) {
    return window
      .fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (json) {
            throw json;
          });
        }
        return response.json();
      });
  }

  // Cart state with the gift metafields already resolved, straight from Liquid.
  function fetchState() {
    var url = rootUrl() + '?section_id=' + SECTION_ID;

    return window
      .fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var data = holder.querySelector('[data-cart-gifts-state]');
        return data ? JSON.parse(data.textContent) : null;
      });
  }

  function pairKey(parentVariantId, giftVariantId) {
    return String(parentVariantId) + ':' + String(giftVariantId);
  }

  /*
   * Diffs the cart against the gifts its line items ask for and returns the
   * list of cart operations needed to make the two match.
   */
  function excludedFromGifts(line, state) {
    var ids = state.excluded_product_ids || [];
    var handles = state.excluded_handles || [];
    if (ids.map(String).indexOf(String(line.product_id)) !== -1) return true;
    if (line.handle && handles.indexOf(line.handle) !== -1) return true;
    return false;
  }

  function buildOperations(state) {
    var desired = {};
    var existing = {};
    var duplicates = [];
    var operations = [];

    // Recompute here. Liquid's array `contains` was keeping excluded products
    // inside eligible_total, so cart value gifts were never removed.
    if (state.lines) {
      state.eligible_total = state.lines.reduce(function (sum, line) {
        if (line.is_gift || excludedFromGifts(line, state)) return sum;
        return sum + (line.line_price || 0);
      }, 0);
    }

    state.lines.forEach(function (line) {
      if (!line.is_gift) return;

      var key = pairKey(line.gift_for, line.id);
      // A gift pair only ever needs one line, collapse anything extra.
      if (existing[key]) {
        duplicates.push(line);
      } else {
        existing[key] = line;
      }
    });

    var hasProductGifts = state.lines.some(function (line) {
      return !line.is_gift && line.gifts && line.gifts.length;
    });

    // Each level adds its own gifts on top of the levels below it.
    var levels = [
      {
        parent: CART_VALUE_PARENT,
        threshold: state.threshold,
        gifts: state.threshold_gifts || [],
        configured: state.threshold_gifts_configured
      },
      {
        parent: CART_VALUE_PARENT_2,
        threshold: state.threshold_2,
        gifts: state.threshold_gifts_2 || [],
        configured: state.threshold_gifts_2_configured
      }
    ];

    // Cart value gifts only when no product metafield gift is active.
    var reachedLevels = hasProductGifts
      ? []
      : levels.filter(function (level) {
          return level.gifts.length > 0 && level.threshold > 0 && state.eligible_total >= level.threshold;
        });
    var thresholdReached = reachedLevels.length > 0;

    reachedLevels.forEach(function (level) {
      if (level.configured > level.gifts.length) {
        // sections/api-cart-gifts.liquid leaves out gifts that are sold out or
        // have no available variant.
        console.warn(
          '[free-gift] ' + (level.configured - level.gifts.length) +
            ' cart value gift(s) skipped: product unavailable or out of stock'
        );
      }

      level.gifts.forEach(function (giftVariantId) {
        desired[pairKey(level.parent, giftVariantId)] = {
          parent: level.parent,
          gift: giftVariantId,
          quantity: 1
        };
      });
    });

    state.lines.forEach(function (line) {
      if (thresholdReached) return;
      if (line.is_gift || !line.gifts || !line.gifts.length) return;

      line.gifts.forEach(function (giftVariantId) {
        var key = pairKey(line.id, giftVariantId);
        if (!desired[key]) {
          desired[key] = { parent: line.id, gift: giftVariantId, quantity: 0 };
        }
        // Two of the granting product means two gifts.
        desired[key].quantity += line.quantity;
      });
    });

    duplicates.forEach(function (line) {
      operations.push({ type: 'change', id: line.key, quantity: 0 });
    });

    Object.keys(existing).forEach(function (key) {
      var line = existing[key];
      var want = desired[key];

      if (!want) {
        // Parent is gone from the cart, or a cart value gift took over.
        operations.push({ type: 'change', id: line.key, quantity: 0 });
      } else if (want.quantity !== line.quantity) {
        operations.push({ type: 'change', id: line.key, quantity: want.quantity });
      }
    });

    Object.keys(desired).forEach(function (key) {
      if (existing[key] || desired[key].quantity < 1) return;

      operations.push({
        type: 'add',
        id: desired[key].gift,
        quantity: desired[key].quantity,
        parent: desired[key].parent
      });
    });

    return operations;
  }

  function runOperation(operation) {
    if (operation.type === 'change') {
      return postJSON(cartUrl() + '/change.js', {
        id: operation.id,
        quantity: operation.quantity
      });
    }

    var properties = {};
    properties[GIFT_PROPERTY] = String(operation.parent);

    return postJSON(cartUrl() + '/add.js', {
      items: [
        {
          id: operation.id,
          quantity: operation.quantity,
          properties: properties
        }
      ]
    });
  }

  // The cart API rejects concurrent writes, so the operations run one by one.
  function runOperations(operations) {
    return operations.reduce(function (chain, operation) {
      return chain.then(function () {
        return runOperation(operation).catch(function (error) {
          // A gift that can no longer be added (sold out, deleted) must not
          // block the rest of the cart.
          console.warn('[free-gift] could not update gift line', operation, error);
        });
      });
    }, Promise.resolve());
  }

  function refreshCartUI() {
    return window
      .fetch(cartUrl() + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        // Flagged so the listener below doesn't treat our own refresh as a
        // cart change that needs another reconciliation pass.
        selfDispatch = true;
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true })
        );
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        selfDispatch = false;
      })
      .catch(function (error) {
        console.error(error);
      });
  }

  /*
   * Reconciles until the cart matches, re-reading it after every round of
   * writes. Another tab (or a page left mid-sync) can write gift lines at the
   * same time, and /cart/add.js merges into an existing gift line instead of
   * creating a second one -- that's how a gift ended up with quantity 2. The
   * re-read catches it and sets the quantity back to 1.
   */
  function reconcile(pass, changed) {
    return fetchState().then(function (state) {
      if (!state || !state.lines) return changed;

      var operations = buildOperations(state);
      if (!operations.length) return changed;

      if (pass >= MAX_PASSES) {
        console.warn('[free-gift] cart still out of sync after ' + MAX_PASSES + ' passes', operations);
        return changed;
      }

      return runOperations(operations).then(function () {
        return reconcile(pass + 1, true);
      });
    });
  }

  function sync() {
    if (syncing) {
      rerun = true;
      return pending;
    }
    syncing = true;

    pending = reconcile(0, false)
      .then(function (changed) {
        if (changed) return refreshCartUI();
      })
      .catch(function (error) {
        console.error('[free-gift]', error);
      })
      .then(function () {
        syncing = false;
        if (rerun) {
          rerun = false;
          return sync();
        }
      });

    return pending;
  }

  // `theme:cart:change` is fired by the theme after every add, quantity change
  // and removal, and once on every page show.
  document.addEventListener('theme:cart:change', function () {
    if (selfDispatch) return;

    // A change landing mid-sync (the shopper kept clicking) is picked up by a
    // second pass once the current one settles.
    if (syncing) {
      rerun = true;
      return;
    }

    sync();
  });

  // Another tab may have changed the cart while this one was in the background.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') sync();
  });

  /*
   * Last check before checkout: the cart drawer and the cart page submit their
   * form with a `checkout` button. Hold the submit until the gifts are synced
   * so a doubled gift line never reaches the order. A sync that hangs must not
   * block the purchase, so checkout goes ahead after CHECKOUT_WAIT ms anyway.
   */
  var checkoutReady = false;

  document.addEventListener(
    'submit',
    function (event) {
      var submitter = event.submitter;
      if (checkoutReady || !submitter || submitter.name !== 'checkout') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var form = event.target;
      submitter.disabled = true;

      var timeout = new Promise(function (resolve) {
        setTimeout(resolve, CHECKOUT_WAIT);
      });

      Promise.race([sync(), timeout]).then(function () {
        checkoutReady = true;
        submitter.disabled = false;

        // Keep `checkout` in the posted data, form.submit() alone drops the
        // submitter and the cart would just reload instead of redirecting.
        var field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'checkout';
        field.value = submitter.value || '1';
        form.appendChild(field);
        form.submit();
      });
    },
    true
  );

  // Coming back from checkout with the back button restores this page from
  // the cache, so the next checkout has to be checked again.
  window.addEventListener('pageshow', function () {
    checkoutReady = false;
  });

  window.theme = window.theme || {};
  window.theme.freeGift = { sync: sync, buildOperations: buildOperations };
})();
